/**
 * The pictures on status, damage and misc tokens that have no art file (Burn shows a flame, Bleed a drop of
 * blood...). Drawn on a 256px chip, inside its ring. On the table a counter's number sits on top, so each
 * picture is big enough to show around the number. Generic tokens (Status Token 1, Misc Counter...) and
 * tier tokens have no picture here and keep their letters.
 */

type Ctx = CanvasRenderingContext2D;

const EDGE = 'rgba(16,12,8,0.88)';

/** Fill the current path, then outline it dark so it reads on any chip color. */
function inked(ctx: Ctx, fill: string, edge = 6) {
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = edge;
  ctx.strokeStyle = EDGE;
  ctx.stroke();
}

/** Stroke the current path twice: a dark border, then the color on top. */
function line(ctx: Ctx, color: string, width: number) {
  ctx.lineWidth = width + 8;
  ctx.strokeStyle = EDGE;
  ctx.stroke();
  ctx.lineWidth = width;
  ctx.strokeStyle = color;
  ctx.stroke();
}

function flamePath(ctx: Ctx, cx: number, cy: number, s: number) {
  ctx.beginPath();
  ctx.moveTo(cx, cy - s);
  ctx.bezierCurveTo(cx + s * 0.3, cy - s * 0.5, cx + s * 0.8, cy - s * 0.25, cx + s * 0.62, cy + s * 0.42);
  ctx.bezierCurveTo(cx + s * 0.5, cy + s * 0.88, cx - s * 0.5, cy + s * 0.88, cx - s * 0.62, cy + s * 0.42);
  ctx.bezierCurveTo(cx - s * 0.74, cy + s * 0.02, cx - s * 0.42, cy - s * 0.22, cx - s * 0.22, cy - s * 0.58);
  ctx.bezierCurveTo(cx - s * 0.08, cy - s * 0.3, cx + s * 0.02, cy - s * 0.62, cx, cy - s);
  ctx.closePath();
}

function heartPath(ctx: Ctx, cx: number, cy: number, s: number) {
  ctx.beginPath();
  ctx.moveTo(cx, cy + s * 0.9);
  ctx.bezierCurveTo(cx - s * 1.45, cy - s * 0.05, cx - s * 0.75, cy - s * 1.15, cx, cy - s * 0.45);
  ctx.bezierCurveTo(cx + s * 0.75, cy - s * 1.15, cx + s * 1.45, cy - s * 0.05, cx, cy + s * 0.9);
  ctx.closePath();
}

function shieldPath(ctx: Ctx, cx: number, cy: number, w: number, h: number) {
  ctx.beginPath();
  ctx.moveTo(cx - w, cy - h * 0.72);
  ctx.quadraticCurveTo(cx - w * 0.4, cy - h * 0.78, cx, cy - h);
  ctx.quadraticCurveTo(cx + w * 0.4, cy - h * 0.78, cx + w, cy - h * 0.72);
  ctx.lineTo(cx + w, cy - h * 0.05);
  ctx.quadraticCurveTo(cx + w * 0.92, cy + h * 0.62, cx, cy + h);
  ctx.quadraticCurveTo(cx - w * 0.92, cy + h * 0.62, cx - w, cy - h * 0.05);
  ctx.closePath();
}

function dropPath(ctx: Ctx, cx: number, cy: number, s: number) {
  // cy is the middle of the round part; the tip points up
  ctx.beginPath();
  ctx.moveTo(cx, cy - s * 2.1);
  ctx.bezierCurveTo(cx + s * 0.5, cy - s * 1.2, cx + s, cy - s * 0.6, cx + s, cy);
  ctx.arc(cx, cy, s, 0, Math.PI);
  ctx.bezierCurveTo(cx - s, cy - s * 0.6, cx - s * 0.5, cy - s * 1.2, cx, cy - s * 2.1);
  ctx.closePath();
}

function starPath(ctx: Ctx, cx: number, cy: number, r: number, points = 5) {
  ctx.beginPath();
  for (let i = 0; i < points * 2; i++) {
    const rr = i % 2 ? r * 0.45 : r;
    const a = (i / (points * 2)) * Math.PI * 2 - Math.PI / 2;
    const x = cx + Math.cos(a) * rr, y = cy + Math.sin(a) * rr;
    i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
  }
  ctx.closePath();
}

function plus(ctx: Ctx, cx: number, cy: number, s: number, fill: string) {
  const t = s * 0.36;
  ctx.beginPath();
  ctx.moveTo(cx - t, cy - s);
  ctx.lineTo(cx + t, cy - s);
  ctx.lineTo(cx + t, cy - t);
  ctx.lineTo(cx + s, cy - t);
  ctx.lineTo(cx + s, cy + t);
  ctx.lineTo(cx + t, cy + t);
  ctx.lineTo(cx + t, cy + s);
  ctx.lineTo(cx - t, cy + s);
  ctx.lineTo(cx - t, cy + t);
  ctx.lineTo(cx - s, cy + t);
  ctx.lineTo(cx - s, cy - t);
  ctx.lineTo(cx - t, cy - t);
  ctx.closePath();
  inked(ctx, fill, 5);
}

/** An arrow pointing up (dir -1) or down (dir 1), centered on cx, cy. */
function arrow(ctx: Ctx, cx: number, cy: number, s: number, dir: 1 | -1, fill: string) {
  ctx.beginPath();
  ctx.moveTo(cx, cy + dir * s);
  ctx.lineTo(cx + s * 0.8, cy);
  ctx.lineTo(cx + s * 0.32, cy);
  ctx.lineTo(cx + s * 0.32, cy - dir * s);
  ctx.lineTo(cx - s * 0.32, cy - dir * s);
  ctx.lineTo(cx - s * 0.32, cy);
  ctx.lineTo(cx - s * 0.8, cy);
  ctx.closePath();
  inked(ctx, fill, 5);
}

/** A skull, s = 1 is the size of the Destroyed token's. */
function skull(ctx: Ctx, cx: number, cy: number, s: number, bone: string, hole = '#17171b') {
  ctx.beginPath();
  ctx.arc(cx, cy - 16 * s, 58 * s, 0, Math.PI * 2);
  ctx.moveTo(cx - 34 * s, cy + 14 * s);
  ctx.roundRect(cx - 34 * s, cy + 14 * s, 68 * s, 52 * s, 12 * s);
  ctx.fillStyle = bone;
  ctx.fill();
  ctx.fillStyle = hole;
  for (const dx of [-24, 24]) {
    ctx.beginPath();
    ctx.ellipse(cx + dx * s, cy - 12 * s, 15 * s, 18 * s, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.beginPath();
  ctx.moveTo(cx, cy + 6 * s);
  ctx.lineTo(cx - 9 * s, cy + 26 * s);
  ctx.lineTo(cx + 9 * s, cy + 26 * s);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = hole;
  ctx.lineWidth = 5 * s;
  ctx.beginPath();
  for (const dx of [-14, 0, 14]) {
    ctx.moveTo(cx + dx * s, cy + 40 * s);
    ctx.lineTo(cx + dx * s, cy + 66 * s);
  }
  ctx.stroke();
}

/** An upright sword, point up, from y0 (tip) to y1 (pommel). broken: the top of the blade snapped off. */
function sword(ctx: Ctx, cx: number, y0: number, y1: number, broken = false) {
  const guard = y0 + (y1 - y0) * 0.68;
  const steel = '#dfe6ee';
  ctx.beginPath();
  if (broken) {
    const snap = y0 + (y1 - y0) * 0.36;
    ctx.moveTo(cx - 12, guard);
    ctx.lineTo(cx - 12, snap + 6);
    ctx.lineTo(cx - 3, snap - 4);
    ctx.lineTo(cx + 4, snap + 8);
    ctx.lineTo(cx + 12, snap - 2);
    ctx.lineTo(cx + 12, guard);
  } else {
    ctx.moveTo(cx - 12, guard);
    ctx.lineTo(cx - 12, y0 + 26);
    ctx.lineTo(cx, y0);
    ctx.lineTo(cx + 12, y0 + 26);
    ctx.lineTo(cx + 12, guard);
  }
  ctx.closePath();
  inked(ctx, steel, 5);
  ctx.beginPath();
  ctx.roundRect(cx - 38, guard - 2, 76, 14, 6);
  inked(ctx, '#c9a24a', 5);
  ctx.beginPath();
  ctx.roundRect(cx - 7, guard + 12, 14, y1 - guard - 26, 4);
  inked(ctx, '#6b4426', 5);
  ctx.beginPath();
  ctx.arc(cx, y1 - 8, 10, 0, Math.PI * 2);
  inked(ctx, '#c9a24a', 5);
}

/** A wing spreading to the right (flip -1 spreads it to the left), its root at x, y. */
function wing(ctx: Ctx, x: number, y: number, s: number, flip: 1 | -1, fill: string) {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(flip * s, s);
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.bezierCurveTo(30, -50, 80, -60, 110, -44);
  ctx.quadraticCurveTo(100, -28, 86, -24);
  ctx.quadraticCurveTo(96, -10, 80, 2);
  ctx.quadraticCurveTo(84, 18, 64, 22);
  ctx.quadraticCurveTo(62, 38, 40, 34);
  ctx.quadraticCurveTo(22, 40, 0, 20);
  ctx.closePath();
  ctx.restore();
  inked(ctx, fill, 5);
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(flip * s, s);
  ctx.beginPath();
  ctx.moveTo(14, 4);
  ctx.quadraticCurveTo(50, -6, 84, -24);
  ctx.moveTo(14, 12);
  ctx.quadraticCurveTo(44, 10, 66, 4);
  ctx.moveTo(14, 18);
  ctx.quadraticCurveTo(34, 24, 46, 22);
  ctx.restore();
  ctx.lineWidth = 4;
  ctx.strokeStyle = 'rgba(16,12,8,0.55)';
  ctx.stroke();
}

function gear(ctx: Ctx, cx: number, cy: number, r: number, teeth: number, fill: string) {
  ctx.beginPath();
  for (let i = 0; i < teeth * 4; i++) {
    const a = (i / (teeth * 4)) * Math.PI * 2;
    const rr = i % 4 < 2 ? r : r * 0.78;
    const x = cx + Math.cos(a) * rr, y = cy + Math.sin(a) * rr;
    i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
  }
  ctx.closePath();
  ctx.moveTo(cx + r * 0.32, cy);
  ctx.arc(cx, cy, r * 0.32, 0, Math.PI * 2, true);
  ctx.fillStyle = fill;
  ctx.fill('evenodd');
  ctx.lineWidth = 5;
  ctx.strokeStyle = EDGE;
  ctx.stroke();
}

/** Draws the picture for a token. Returns false when it has none (it keeps its letters). */
export function drawSymbol(ctx: Ctx, key: string): boolean {
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  const done = draw(ctx, key);
  ctx.restore();
  return done;
}

function draw(ctx: Ctx, key: string): boolean {
  switch (key) {
    case 'burn': {
      flamePath(ctx, 128, 132, 86);
      inked(ctx, '#ff7a1f');
      flamePath(ctx, 128, 160, 50);
      ctx.fillStyle = '#ffd84a';
      ctx.fill();
      flamePath(ctx, 128, 180, 22);
      ctx.fillStyle = '#fff6c8';
      ctx.fill();
      return true;
    }
    case 'bleed': {
      dropPath(ctx, 128, 150, 46);
      inked(ctx, '#c8102e');
      ctx.fillStyle = 'rgba(255,255,255,0.55)';
      ctx.beginPath();
      ctx.ellipse(110, 140, 9, 16, -0.4, 0, Math.PI * 2);
      ctx.fill();
      // two small drops falling beside it
      dropPath(ctx, 186, 196, 10);
      inked(ctx, '#c8102e', 4);
      dropPath(ctx, 72, 186, 8);
      inked(ctx, '#c8102e', 4);
      return true;
    }
    case 'buffer': {
      // a soft bubble cushioning the blow
      ctx.beginPath();
      ctx.arc(128, 132, 76, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(190,235,255,0.35)';
      ctx.fill();
      line(ctx, 'rgba(235,250,255,0.95)', 7);
      ctx.beginPath();
      ctx.arc(128, 132, 56, Math.PI * 1.1, Math.PI * 1.45);
      ctx.lineWidth = 9;
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(160, 168, 8, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.fill();
      return true;
    }
    case 'shield': {
      shieldPath(ctx, 128, 130, 70, 86);
      inked(ctx, '#c9d3de');
      shieldPath(ctx, 128, 130, 48, 62);
      ctx.lineWidth = 6;
      ctx.strokeStyle = 'rgba(40,50,64,0.6)';
      ctx.stroke();
      return true;
    }
    case 'gerzite-shield': {
      shieldPath(ctx, 128, 130, 70, 86);
      inked(ctx, '#c9d3de');
      ctx.beginPath();
      ctx.moveTo(128, 82);
      ctx.lineTo(162, 122);
      ctx.lineTo(128, 172);
      ctx.lineTo(94, 122);
      ctx.closePath();
      inked(ctx, '#3fc6f2', 5);
      ctx.beginPath();
      ctx.moveTo(128, 82);
      ctx.lineTo(128, 172);
      ctx.moveTo(94, 122);
      ctx.lineTo(162, 122);
      ctx.lineWidth = 3;
      ctx.strokeStyle = 'rgba(255,255,255,0.7)';
      ctx.stroke();
      return true;
    }
    case 'overshield': {
      shieldPath(ctx, 146, 118, 58, 74);
      inked(ctx, '#9aa8b8');
      shieldPath(ctx, 112, 142, 58, 74);
      inked(ctx, '#f2c94c');
      plus(ctx, 112, 140, 22, '#ffffff');
      return true;
    }
    case 'thorns': {
      // a thorny bramble curling across the chip
      ctx.beginPath();
      ctx.moveTo(52, 190);
      ctx.bezierCurveTo(90, 90, 170, 190, 206, 70);
      line(ctx, '#5d8a3a', 14);
      const pts: [number, number, number][] = [];
      for (let i = 1; i < 8; i++) {
        const t = i / 8;
        const u = 1 - t;
        const x = u ** 3 * 52 + 3 * u * u * t * 90 + 3 * u * t * t * 170 + t ** 3 * 206;
        const y = u ** 3 * 190 + 3 * u * u * t * 90 + 3 * u * t * t * 190 + t ** 3 * 70;
        const dx = 3 * u * u * (90 - 52) + 6 * u * t * (170 - 90) + 3 * t * t * (206 - 170);
        const dy = 3 * u * u * (90 - 190) + 6 * u * t * (190 - 90) + 3 * t * t * (70 - 190);
        pts.push([x, y, Math.atan2(dy, dx) + (i % 2 ? -Math.PI / 2 : Math.PI / 2)]);
      }
      for (const [x, y, a] of pts) {
        ctx.beginPath();
        ctx.moveTo(x + Math.cos(a + 1.3) * 9, y + Math.sin(a + 1.3) * 9);
        ctx.lineTo(x + Math.cos(a) * 30, y + Math.sin(a) * 30);
        ctx.lineTo(x + Math.cos(a - 1.3) * 9, y + Math.sin(a - 1.3) * 9);
        ctx.closePath();
        inked(ctx, '#e7dcc0', 4);
      }
      return true;
    }
    case 'health': {
      heartPath(ctx, 128, 136, 76);
      inked(ctx, '#e8344a');
      ctx.fillStyle = 'rgba(255,255,255,0.45)';
      ctx.beginPath();
      ctx.ellipse(92, 104, 12, 18, -0.6, 0, Math.PI * 2);
      ctx.fill();
      return true;
    }
    case 'overheal': {
      heartPath(ctx, 120, 142, 66);
      inked(ctx, '#ff6f9c');
      plus(ctx, 182, 78, 28, '#ffe27a');
      return true;
    }
    case 'damage': {
      // a faint red crack
      ctx.beginPath();
      ctx.moveTo(78, 52);
      ctx.lineTo(108, 98);
      ctx.lineTo(96, 126);
      ctx.lineTo(136, 152);
      ctx.lineTo(126, 184);
      ctx.lineTo(170, 210);
      ctx.moveTo(108, 98);
      ctx.lineTo(150, 88);
      ctx.lineTo(172, 64);
      ctx.moveTo(136, 152);
      ctx.lineTo(176, 140);
      ctx.lineTo(200, 156);
      ctx.moveTo(96, 126);
      ctx.lineTo(62, 140);
      ctx.lineWidth = 12;
      ctx.strokeStyle = 'rgba(30,0,0,0.35)';
      ctx.stroke();
      ctx.lineWidth = 5;
      ctx.strokeStyle = 'rgba(255,70,70,0.7)';
      ctx.stroke();
      return true;
    }
    case 'sp-decay': {
      // a crumbling brick: cracked, with chips falling away
      ctx.beginPath();
      ctx.moveTo(66, 92);
      ctx.lineTo(190, 92);
      ctx.lineTo(190, 128);
      ctx.lineTo(176, 140);
      ctx.lineTo(182, 156);
      ctx.lineTo(150, 156);
      ctx.lineTo(140, 146);
      ctx.lineTo(66, 156);
      ctx.closePath();
      inked(ctx, '#b0683e');
      ctx.beginPath();
      ctx.moveTo(120, 92);
      ctx.lineTo(112, 116);
      ctx.lineTo(126, 128);
      ctx.lineTo(118, 150);
      ctx.lineWidth = 5;
      ctx.strokeStyle = EDGE;
      ctx.stroke();
      for (const [x, y, s] of [[160, 182, 12], [184, 196, 8], [138, 204, 7]]) {
        ctx.beginPath();
        ctx.moveTo(x - s, y - s * 0.6);
        ctx.lineTo(x + s, y - s);
        ctx.lineTo(x + s * 0.7, y + s);
        ctx.lineTo(x - s, y + s * 0.7);
        ctx.closePath();
        inked(ctx, '#b0683e', 4);
      }
      return true;
    }
    case 'range': {
      // a drawn bow with an arrow nocked
      ctx.beginPath();
      ctx.arc(170, 128, 92, Math.PI * 0.7, Math.PI * 1.3);
      line(ctx, '#a8743e', 11);
      const top = [170 + Math.cos(Math.PI * 1.3) * 92, 128 + Math.sin(Math.PI * 1.3) * 92];
      const bot = [170 + Math.cos(Math.PI * 0.7) * 92, 128 + Math.sin(Math.PI * 0.7) * 92];
      ctx.beginPath();
      ctx.moveTo(top[0], top[1]);
      ctx.lineTo(118, 128);
      ctx.lineTo(bot[0], bot[1]);
      ctx.lineWidth = 3;
      ctx.strokeStyle = '#f4ead0';
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(108, 128);
      ctx.lineTo(196, 128);
      line(ctx, '#f4ead0', 6);
      ctx.beginPath();
      ctx.moveTo(214, 128);
      ctx.lineTo(190, 114);
      ctx.lineTo(190, 142);
      ctx.closePath();
      inked(ctx, '#dfe6ee', 4);
      return true;
    }
    case 'frozen': {
      // a snowflake
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2 - Math.PI / 2;
        const ex = 128 + Math.cos(a) * 84, ey = 128 + Math.sin(a) * 84;
        ctx.moveTo(128, 128);
        ctx.lineTo(ex, ey);
        for (const [d, l] of [[0.5, 24], [0.75, 16]]) {
          const bx = 128 + Math.cos(a) * 84 * d, by = 128 + Math.sin(a) * 84 * d;
          for (const side of [-0.7, 0.7]) {
            ctx.moveTo(bx, by);
            ctx.lineTo(bx + Math.cos(a + side) * l, by + Math.sin(a + side) * l);
          }
        }
      }
      line(ctx, '#e6f7ff', 8);
      return true;
    }
    case 'stunned': {
      // stars circling a dizzy head
      ctx.beginPath();
      ctx.ellipse(128, 128, 82, 34, -0.15, 0, Math.PI * 2);
      line(ctx, 'rgba(255,240,170,0.9)', 5);
      for (const [x, y, r] of [[52, 132, 24], [150, 98, 28], [196, 150, 20]]) {
        starPath(ctx, x, y, r);
        inked(ctx, '#ffd84a', 5);
      }
      return true;
    }
    case 'weakness': {
      sword(ctx, 112, 50, 206, true);
      // the snapped-off tip, falling away
      ctx.save();
      ctx.translate(176, 82);
      ctx.rotate(0.6);
      ctx.beginPath();
      ctx.moveTo(-12, 22);
      ctx.lineTo(-12, -8);
      ctx.lineTo(0, -32);
      ctx.lineTo(12, -8);
      ctx.lineTo(12, 18);
      ctx.lineTo(4, 26);
      ctx.closePath();
      ctx.restore();
      inked(ctx, '#dfe6ee', 5);
      arrow(ctx, 186, 168, 28, 1, '#a46ad8');
      return true;
    }
    case 'damage-boost': {
      sword(ctx, 112, 44, 210);
      arrow(ctx, 186, 92, 30, -1, '#7ee06a');
      return true;
    }
    case 'susceptible': {
      // a cracked shield
      shieldPath(ctx, 128, 130, 70, 86);
      inked(ctx, '#c9d3de');
      ctx.beginPath();
      ctx.moveTo(122, 46);
      ctx.lineTo(138, 92);
      ctx.lineTo(112, 122);
      ctx.lineTo(146, 156);
      ctx.lineTo(128, 214);
      ctx.moveTo(112, 122);
      ctx.lineTo(80, 118);
      ctx.moveTo(146, 156);
      ctx.lineTo(178, 150);
      ctx.lineWidth = 7;
      ctx.strokeStyle = '#2a1414';
      ctx.stroke();
      return true;
    }
    case 'decay': {
      skull(ctx, 128, 118, 0.95, '#b6d987', '#1d2a12');
      // dripping rot
      for (const [x, y, s] of [[86, 198, 8], [170, 204, 10], [128, 222, 7]]) {
        dropPath(ctx, x, y, s);
        inked(ctx, '#8cc152', 3);
      }
      return true;
    }
    case 'transmission': {
      skull(ctx, 128, 128, 0.62, '#d7e8c2', '#1d2a12');
      // spores drifting off it
      for (const [x, y, r] of [[62, 74, 10], [196, 70, 12], [204, 150, 9], [56, 160, 11], [128, 46, 8], [88, 206, 8], [176, 204, 10]]) {
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        inked(ctx, '#9ad15a', 4);
      }
      return true;
    }
    case 'regeneration': {
      // a leaf with a plus
      ctx.beginPath();
      ctx.moveTo(60, 196);
      ctx.bezierCurveTo(50, 110, 120, 52, 204, 56);
      ctx.bezierCurveTo(206, 140, 150, 202, 60, 196);
      ctx.closePath();
      inked(ctx, '#5cc46a');
      ctx.beginPath();
      ctx.moveTo(60, 196);
      ctx.quadraticCurveTo(120, 140, 188, 72);
      ctx.lineWidth = 5;
      ctx.strokeStyle = 'rgba(16,40,16,0.7)';
      ctx.stroke();
      plus(ctx, 176, 172, 26, '#ffffff');
      return true;
    }
    case 'divinity': {
      // a halo over spread wings
      wing(ctx, 132, 146, 0.9, 1, '#fff7e0');
      wing(ctx, 124, 146, 0.9, -1, '#fff7e0');
      ctx.beginPath();
      ctx.ellipse(128, 74, 46, 16, 0, 0, Math.PI * 2);
      line(ctx, '#ffd84a', 9);
      return true;
    }
    case 'comatose': {
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.lineWidth = 8;
      ctx.strokeStyle = EDGE;
      ctx.fillStyle = '#e8e4ff';
      for (const [x, y, size] of [[94, 160, 84], [150, 112, 62], [188, 74, 44]]) {
        ctx.font = `700 ${size}px Georgia, serif`;
        ctx.strokeText('Z', x, y);
        ctx.fillText('Z', x, y);
      }
      return true;
    }
    case 'crumble': {
      // a rock breaking apart
      const chunks: [number, number][][] = [
        [[70, 104], [118, 74], [126, 132], [80, 150]],
        [[132, 72], [184, 96], [176, 138], [136, 132]],
        [[82, 160], [128, 140], [140, 190], [96, 196]],
        [[146, 146], [180, 150], [190, 186], [152, 194]],
      ];
      for (const pts of chunks) {
        ctx.beginPath();
        pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
        ctx.closePath();
        inked(ctx, '#9b8f80', 5);
      }
      for (const [x, y, r] of [[200, 206, 7], [62, 192, 6], [118, 214, 5]]) {
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        inked(ctx, '#9b8f80', 3);
      }
      return true;
    }
    case 'possession': {
      // a ghost
      ctx.beginPath();
      ctx.moveTo(70, 196);
      ctx.lineTo(70, 116);
      ctx.arc(128, 116, 58, Math.PI, 0);
      ctx.lineTo(186, 196);
      for (let i = 0; i < 4; i++) {
        const x0 = 186 - i * 29;
        ctx.quadraticCurveTo(x0 - 7, 176, x0 - 14.5, 190);
        ctx.quadraticCurveTo(x0 - 22, 206, x0 - 29, 196);
      }
      ctx.closePath();
      inked(ctx, 'rgba(236,240,255,0.94)');
      ctx.fillStyle = '#1a1630';
      for (const x of [106, 150]) {
        ctx.beginPath();
        ctx.ellipse(x, 116, 11, 16, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.beginPath();
      ctx.ellipse(128, 152, 12, 9, 0, 0, Math.PI * 2);
      ctx.fill();
      return true;
    }
    case 'overclock': {
      gear(ctx, 118, 136, 74, 9, '#c9d3de');
      // a spark of extra speed
      ctx.beginPath();
      ctx.moveTo(196, 42);
      ctx.lineTo(166, 96);
      ctx.lineTo(186, 96);
      ctx.lineTo(164, 146);
      ctx.lineTo(214, 82);
      ctx.lineTo(192, 82);
      ctx.lineTo(214, 42);
      ctx.closePath();
      inked(ctx, '#ffd84a', 5);
      return true;
    }
    case 'frenzy': {
      // two crossed claw slashes
      for (const [x0, y0, cx, cy, x1, y1] of [
        [60, 64, 150, 96, 196, 196],
        [196, 64, 106, 96, 60, 196],
      ]) {
        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.quadraticCurveTo(cx, cy, x1, y1);
        line(ctx, '#ff4b4b', 14);
        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.quadraticCurveTo(cx, cy, x1, y1);
        ctx.lineWidth = 4;
        ctx.strokeStyle = 'rgba(255,230,220,0.9)';
        ctx.stroke();
      }
      return true;
    }
    case 'ambush': {
      // an eye peering over a bush
      ctx.beginPath();
      ctx.moveTo(62, 112);
      ctx.quadraticCurveTo(128, 50, 194, 112);
      ctx.quadraticCurveTo(128, 150, 62, 112);
      ctx.closePath();
      inked(ctx, '#f4ead0');
      ctx.beginPath();
      ctx.arc(128, 108, 22, 0, Math.PI * 2);
      ctx.fillStyle = '#2b1d0e';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(120, 100, 6, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(44, 206);
      for (const [x, r] of [[66, 30], [110, 38], [156, 36], [196, 28]]) ctx.arc(x, 172, r, Math.PI, 0);
      ctx.lineTo(220, 206);
      ctx.closePath();
      inked(ctx, '#3f7a35');
      return true;
    }
    case 'darkgale': {
      // a dark whirlwind
      const rings: [number, number, number][] = [[128, 62, 74], [134, 96, 56], [126, 128, 42], [132, 158, 28], [126, 186, 16]];
      for (const [x, y, r] of rings) {
        ctx.beginPath();
        ctx.ellipse(x, y, r, r * 0.26, 0, 0, Math.PI * 2);
        line(ctx, '#9b6cff', 7);
      }
      ctx.beginPath();
      ctx.moveTo(126, 202);
      ctx.lineTo(120, 214);
      line(ctx, '#9b6cff', 6);
      return true;
    }
    case 'flying': {
      wing(ctx, 70, 150, 1.2, 1, '#f4f0e6');
      return true;
    }
    case 'lanespanner': {
      // a double arrow across two lane lines
      ctx.beginPath();
      ctx.moveTo(98, 62);
      ctx.lineTo(98, 194);
      ctx.moveTo(158, 62);
      ctx.lineTo(158, 194);
      ctx.setLineDash([14, 12]);
      ctx.lineWidth = 6;
      ctx.strokeStyle = 'rgba(255,255,255,0.6)';
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(50, 128);
      ctx.lineTo(84, 98);
      ctx.lineTo(84, 116);
      ctx.lineTo(172, 116);
      ctx.lineTo(172, 98);
      ctx.lineTo(206, 128);
      ctx.lineTo(172, 158);
      ctx.lineTo(172, 140);
      ctx.lineTo(84, 140);
      ctx.lineTo(84, 158);
      ctx.closePath();
      inked(ctx, '#f4ead0', 5);
      return true;
    }
    case 'splash': {
      // a drop hitting and spraying outward
      ctx.beginPath();
      ctx.ellipse(128, 168, 70, 18, 0, 0, Math.PI * 2);
      line(ctx, '#bfe6ff', 6);
      dropPath(ctx, 128, 128, 26);
      inked(ctx, '#6cc4ff');
      for (const [x, y, a] of [[64, 120, -0.9], [192, 120, 0.9], [84, 84, -0.5], [172, 84, 0.5]]) {
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(a);
        dropPath(ctx, 0, 0, 9);
        ctx.restore();
        inked(ctx, '#6cc4ff', 4);
      }
      return true;
    }
    case 'link': {
      // two chain links
      ctx.save();
      ctx.translate(128, 128);
      ctx.rotate(-Math.PI / 4);
      for (const x of [-34, 34]) {
        ctx.beginPath();
        ctx.roundRect(x - 46, -24, 92, 48, 24);
        line(ctx, '#d9dee6', 12);
      }
      // the first link passes over the second where they cross
      ctx.beginPath();
      ctx.moveTo(-12, -24);
      ctx.lineTo(12, -24);
      line(ctx, '#d9dee6', 12);
      ctx.restore();
      return true;
    }
    case 'resource': {
      // a sheaf of wheat
      ctx.beginPath();
      ctx.moveTo(128, 214);
      ctx.lineTo(128, 52);
      line(ctx, '#d9b25a', 6);
      for (let i = 0; i < 5; i++) {
        const y = 66 + i * 26;
        for (const side of [-1, 1]) {
          ctx.beginPath();
          ctx.ellipse(128 + side * 18, y + 8, 11, 20, side * 0.6, 0, Math.PI * 2);
          inked(ctx, '#f0c95c', 4);
        }
      }
      ctx.beginPath();
      ctx.ellipse(128, 48, 10, 18, 0, 0, Math.PI * 2);
      inked(ctx, '#f0c95c', 4);
      return true;
    }
    case 'life-bank': {
      // a heart behind a vault door
      ctx.beginPath();
      ctx.roundRect(52, 52, 152, 152, 18);
      inked(ctx, '#6d7782');
      ctx.beginPath();
      ctx.arc(128, 128, 58, 0, Math.PI * 2);
      inked(ctx, '#9aa6b2', 5);
      ctx.fillStyle = '#2b3138';
      for (const [x, y] of [[66, 66], [190, 66], [66, 190], [190, 190]]) {
        ctx.beginPath();
        ctx.arc(x, y, 5, 0, Math.PI * 2);
        ctx.fill();
      }
      // the door's handle spokes
      ctx.beginPath();
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
        ctx.moveTo(128 + Math.cos(a) * 46, 128 + Math.sin(a) * 46);
        ctx.lineTo(128 + Math.cos(a) * 66, 128 + Math.sin(a) * 66);
      }
      ctx.lineWidth = 7;
      ctx.strokeStyle = '#2b3138';
      ctx.stroke();
      heartPath(ctx, 128, 132, 34);
      inked(ctx, '#e8344a', 5);
      return true;
    }
    default:
      return false;
  }
}
